import {
  isKeepAlive,
  RELAY_NOTICE_EVENT,
  RUN_ERROR,
  RUN_STARTED,
  compareEventIds,
  runStartedHandle,
  terminalReason,
  type IntelligenceFrame,
  type RelayNotice,
} from "@/features/intelligence/api/frames";
import type {
  IntelligenceAttachmentRef,
  IntelligenceConversation,
  IntelligenceMessageNode,
} from "@/features/intelligence/api/shapes";
import {
  approvalRequestOf,
  approvalSettledBy,
  unactionableApprovalFrame,
  type IntelligenceApprovalRequest,
} from "@/features/intelligence/lib/approval-frame";
import {
  entitiesInvalidatedBy,
  mergeInvalidated,
  publishEntityInvalidation,
  type InvalidatedEntity,
} from "@/shared/events/entity-invalidation";
import { hasResult, isUnresolved } from "@/features/intelligence/lib/tool-outcome";

export interface ToolActivity {
  toolCallId: string;
  toolName: string;
  argsText: string;
  outcome: string | null;
  running: boolean;
}

export interface RunErrorDetail {
  message: string | null;
  code: string | null;
  correlationId: string | null;
}

function runErrorDetail(frame: IntelligenceFrame): RunErrorDetail | null {
  if (frame.event !== RUN_ERROR) return null;
  const framePayload = payload(frame);
  const detail: RunErrorDetail = {
    message: framePayload.str("message") ?? framePayload.str("detail"),
    code: framePayload.str("code"),
    correlationId: framePayload.str("correlationId") ?? framePayload.str("correlation_id"),
  };
  if (!detail.message && !detail.code && !detail.correlationId) return null;
  return detail;
}

export interface ThreadNode {
  id: string;
  role: "user" | "assistant";
  text: string;
  truncated: boolean;
  errored: boolean;
  streaming: boolean;
  tools: ToolActivity[];
  attachments: IntelligenceAttachmentRef[];
}

export type TurnOutcome = "idle" | "running" | "complete" | "truncated" | "errored" | "interrupted";

export interface ThreadState {
  nodes: ThreadNode[];
  runId: string | null;
  threadId: string | null;
  lastEventId: string | null;
  frames: number;
  keepAlives: number;
  outcome: TurnOutcome;
  cancelled: boolean;
  approval: IntelligenceApprovalRequest | null;
  approvalUnactionable: boolean;
  notice: RelayNotice | null;
  activeConfirmed: boolean;
  runError: RunErrorDetail | null;
  invalidated: InvalidatedEntity[];
  toolStartedAt: Record<string, number>;
  healedTools: string[];
}

const PENDING_USER_ID = "pending:user";

export function newThreadState(nodes: ThreadNode[] = []): ThreadState {
  return {
    nodes,
    runId: null,
    threadId: null,
    lastEventId: null,
    frames: 0,
    keepAlives: 0,
    outcome: "idle",
    cancelled: false,
    approval: null,
    approvalUnactionable: false,
    notice: null,
    activeConfirmed: false,
    runError: null,
    invalidated: [],
    toolStartedAt: {},
    healedTools: [],
  };
}

function selectedPath(
  messages: IntelligenceMessageNode[],
  currentNodeId: string | null,
): IntelligenceMessageNode[] {
  if (!currentNodeId) return messages;
  const byId = new Map<string, IntelligenceMessageNode>(messages.map((m) => [m.id, m]));
  if (!byId.has(currentNodeId)) return messages;
  const path: IntelligenceMessageNode[] = [];
  const seen = new Set<string>();
  let cursor: string | null = currentNodeId;
  while (cursor && byId.has(cursor) && !seen.has(cursor)) {
    seen.add(cursor);
    const node = byId.get(cursor);
    if (!node) break;
    path.push(node);
    cursor = node.parentId;
  }
  return path.reverse();
}

function toThreadNode(
  node: IntelligenceMessageNode,
  activeRunId: string | null = null,
): ThreadNode {
  const live = activeRunId !== null && node.id === activeRunId;
  return {
    id: node.id,
    role: node.role === "user" ? "user" : "assistant",
    text: node.content,
    truncated: node.cancelled,
    errored: node.errored,
    streaming: false,
    tools: node.toolCalls.map((call) => ({
      toolCallId: call.toolCallId,
      toolName: call.toolName,
      argsText: safeArgs(call.args),
      outcome: call.outcome,
      running: live && call.outcome === null,
    })),
    attachments: node.attachments,
  };
}

function safeArgs(args: Record<string, unknown>): string {
  try {
    return Object.keys(args).length === 0 ? "" : JSON.stringify(args, null, 2);
  } catch {
    return "";
  }
}

export function hydrateThread(conversation: IntelligenceConversation): ThreadState {
  const activeRunId = conversation.activeTurn?.runId ?? null;
  const state = newThreadState(
    selectedPath(conversation.messages, conversation.currentNodeId).map((node) =>
      toThreadNode(node, activeRunId),
    ),
  );
  state.threadId = conversation.id;
  if (conversation.activeTurn) {
    state.runId = conversation.activeTurn.runId;
    state.outcome = "running";
  }
  return state;
}

export function beginTurn(
  state: ThreadState,
  content: string,
  attachments: readonly IntelligenceAttachmentRef[] = [],
): ThreadState {
  return {
    ...state,
    nodes: [
      ...state.nodes.filter((node) => node.id !== PENDING_USER_ID),
      {
        id: PENDING_USER_ID,
        role: "user",
        text: content,
        truncated: false,
        errored: false,
        streaming: false,
        tools: [],
        attachments: [...attachments],
      },
    ],
    runId: null,
    lastEventId: null,
    frames: 0,
    keepAlives: 0,
    outcome: "running",
    cancelled: false,
    approval: null,
    approvalUnactionable: false,
    notice: null,
    activeConfirmed: false,
    runError: null,
  };
}

export function beginRegenerate(state: ThreadState): ThreadState {
  return {
    ...state,
    runId: null,
    lastEventId: null,
    frames: 0,
    keepAlives: 0,
    outcome: "running",
    cancelled: false,
    approval: null,
    approvalUnactionable: false,
    notice: null,
    activeConfirmed: false,
    runError: null,
  };
}

export function abandonTurn(state: ThreadState): ThreadState {
  return {
    ...state,
    nodes: state.nodes.filter((node) => node.id !== PENDING_USER_ID),
    outcome: "idle",
    runId: null,
  };
}

export function loseStream(state: ThreadState): ThreadState {
  if (state.outcome !== "running") return state;
  return {
    ...state,
    outcome: "interrupted",
    activeConfirmed: false,
    nodes: state.nodes.map((node) => (node.streaming ? { ...node, streaming: false } : node)),
  };
}

interface FramePayload {
  data: Record<string, unknown>;
  str(key: string): string | null;
}

function payload(frame: IntelligenceFrame): FramePayload {
  let data: Record<string, unknown> = {};
  if (frame.data !== "") {
    try {
      const body = JSON.parse(frame.data) as unknown;
      if (body !== null && typeof body === "object" && !Array.isArray(body)) {
        data = body as Record<string, unknown>;
      }
    } catch {}
  }
  return {
    data,
    str(key) {
      const value = data[key];
      return typeof value === "string" && value !== "" ? value : null;
    },
  };
}

function ensureAssistant(nodes: ThreadNode[], id: string): ThreadNode[] {
  if (nodes.some((node) => node.id === id && node.role === "assistant")) return nodes;
  return [
    ...nodes,
    {
      id,
      role: "assistant",
      text: "",
      truncated: false,
      errored: false,
      streaming: true,
      tools: [],
      attachments: [],
    },
  ];
}

function mapThreadNode(
  nodes: ThreadNode[],
  id: string,
  transform: (node: ThreadNode) => ThreadNode,
): ThreadNode[] {
  return nodes.map((node) => (node.id === id ? transform(node) : node));
}

function mapToolActivity(
  node: ThreadNode,
  toolCallId: string,
  transform: (tool: ToolActivity) => ToolActivity,
): ThreadNode {
  const known = node.tools.some((tool) => tool.toolCallId === toolCallId);
  const tools = known
    ? node.tools.map((tool) => (tool.toolCallId === toolCallId ? transform(tool) : tool))
    : [
        ...node.tools,
        transform({ toolCallId, toolName: "", argsText: "", outcome: null, running: true }),
      ];
  return { ...node, tools };
}

export function endAllCalls(state: ThreadState): ThreadState {
  return { ...state, nodes: endCalls(state.nodes) };
}

export function runGoneUpstream(state: ThreadState): ThreadState {
  return {
    ...state,
    notice: {
      reason: "reattach-failed",
      detail: "",
      runId: state.runId,
      lastEventId: state.lastEventId,
      status: 404,
    },
    nodes: endCalls(state.nodes),
  };
}

function endCalls(nodes: ThreadNode[]): ThreadNode[] {
  return nodes.map((node) =>
    node.tools.some((tool) => tool.running)
      ? { ...node, tools: node.tools.map((tool) => ({ ...tool, running: false })) }
      : node,
  );
}

export function applyFrame(
  state: ThreadState,
  frame: IntelligenceFrame,
  now: number = Date.now(),
): ThreadState {
  if (isKeepAlive(frame)) {
    return { ...state, keepAlives: state.keepAlives + 1 };
  }

  const framePayload = payload(frame);

  if (frame.event === RELAY_NOTICE_EVENT) {
    const notice = framePayload.data as unknown as RelayNotice;
    return {
      ...state,
      frames: state.frames + 1,
      notice,
      outcome: state.outcome === "running" ? "interrupted" : state.outcome,
      activeConfirmed: false,
      runId: state.runId ?? notice.runId ?? null,
      lastEventId: state.lastEventId ?? notice.lastEventId ?? null,
      nodes: (upstreamHasNoRun(notice) ? endCalls(state.nodes) : state.nodes).map((node) =>
        node.streaming ? { ...node, streaming: false } : node,
      ),
    };
  }

  const handle = frame.event === RUN_STARTED ? runStartedHandle(frame) : null;
  const newRun = handle !== null && handle.runId !== state.runId;

  if (!newRun && frame.id !== null && state.lastEventId !== null) {
    const order = compareEventIds(frame.id, state.lastEventId);
    if (order !== null && order <= 0) return state;
  }

  let next: ThreadState = {
    ...state,
    frames: state.frames + 1,
    lastEventId: frame.id !== null ? frame.id : state.lastEventId,
  };
  if (newRun) {
    next = {
      ...next,
      lastEventId: frame.id,
      cancelled: false,
      keepAlives: 0,
      notice: null,
      runError: null,
      nodes: endCalls(next.nodes),
    };
  }

  switch (frame.event) {
    case RUN_STARTED: {
      if (!handle) break;
      next.runId = handle.runId;
      next.threadId = handle.threadId ?? next.threadId;
      next.outcome = "running";
      break;
    }

    case "CUSTOM": {
      const name = framePayload.str("name");
      if (name === "USER_NODE_CREATED") {
        const value =
          framePayload.data.value !== null && typeof framePayload.data.value === "object"
            ? (framePayload.data.value as Record<string, unknown>)
            : {};
        const id = typeof value.id === "string" ? value.id : "";
        const content = typeof value.content === "string" ? value.content : "";
        if (id) {
          const pending = next.nodes.find((node) => node.id === PENDING_USER_ID);
          const withoutPending = next.nodes.filter((node) => node.id !== PENDING_USER_ID);
          next.nodes = withoutPending.some((node) => node.id === id)
            ? withoutPending
            : [
                ...withoutPending,
                {
                  id,
                  role: "user",
                  text: content,
                  truncated: false,
                  errored: false,
                  streaming: false,
                  tools: [],
                  attachments: pending?.attachments ?? [],
                },
              ];
        }
        break;
      }
      if (name === "CANCELLED") {
        next.cancelled = true;
        next.approval = null;
        next.approvalUnactionable = false;
        if (next.runId) {
          next.nodes = mapThreadNode(next.nodes, next.runId, (node) => ({
            ...node,
            truncated: true,
          }));
        }
        break;
      }
      if (applyInvalidation(next, frame)) break;
      applyApproval(next, frame);
      break;
    }

    case "TEXT_MESSAGE_START": {
      const id = framePayload.str("messageId") ?? next.runId;
      if (!id) break;
      next.nodes = ensureAssistant(next.nodes, id);
      next.nodes = mapThreadNode(next.nodes, id, (node) => ({ ...node, streaming: true }));
      break;
    }

    case "TEXT_MESSAGE_CONTENT": {
      const id = framePayload.str("messageId") ?? next.runId;
      const delta = typeof framePayload.data.delta === "string" ? framePayload.data.delta : "";
      if (!id || delta === "") break;
      next.nodes = ensureAssistant(next.nodes, id);
      next.nodes = mapThreadNode(next.nodes, id, (node) => ({
        ...node,
        text: node.text + delta,
        streaming: true,
      }));
      break;
    }

    case "TEXT_MESSAGE_END": {
      const id = framePayload.str("messageId") ?? next.runId;
      if (!id) break;
      next.nodes = mapThreadNode(next.nodes, id, (node) => ({ ...node, streaming: false }));
      break;
    }

    case "TOOL_CALL_START": {
      const toolCallId = framePayload.str("toolCallId");
      const nodeId = framePayload.str("messageId") ?? next.runId;
      if (!toolCallId || !nodeId) break;
      next.nodes = ensureAssistant(next.nodes, nodeId);
      next.nodes = mapThreadNode(next.nodes, nodeId, (node) =>
        mapToolActivity(node, toolCallId, (tool) => ({
          ...tool,
          toolName:
            framePayload.str("toolCallName") ?? framePayload.str("toolName") ?? tool.toolName,
          running: true,
        })),
      );
      if (!(toolCallId in next.toolStartedAt)) {
        next.toolStartedAt = { ...next.toolStartedAt, [toolCallId]: now };
      }
      break;
    }

    case "TOOL_CALL_ARGS": {
      const toolCallId = framePayload.str("toolCallId");
      const nodeId = framePayload.str("messageId") ?? next.runId;
      const delta = typeof framePayload.data.delta === "string" ? framePayload.data.delta : "";
      if (!toolCallId || !nodeId) break;
      next.nodes = ensureAssistant(next.nodes, nodeId);
      next.nodes = mapThreadNode(next.nodes, nodeId, (node) =>
        mapToolActivity(node, toolCallId, (tool) => ({
          ...tool,
          argsText: tool.argsText + delta,
        })),
      );
      break;
    }

    case "TOOL_CALL_END": {
      settleApproval(next, frame);
      break;
    }

    case "TOOL_CALL_RESULT": {
      const toolCallId = framePayload.str("toolCallId");
      const nodeId = framePayload.str("messageId") ?? next.runId;
      if (!toolCallId || !nodeId) break;
      next.nodes = ensureAssistant(next.nodes, nodeId);
      next.nodes = mapThreadNode(next.nodes, nodeId, (node) =>
        mapToolActivity(node, toolCallId, (tool) => ({
          ...tool,
          outcome: framePayload.str("content"),
          running: false,
        })),
      );
      settleApproval(next, frame);
      break;
    }

    default: {
      if (applyInvalidation(next, frame)) break;
      applyApproval(next, frame);
      break;
    }
  }

  const terminal = terminalReason(frame);
  if (terminal) {
    next.outcome =
      terminal === "finished" ? (next.cancelled ? "truncated" : "complete") : "errored";
    next.runError = runErrorDetail(frame) ?? next.runError;
    next.approval = null;
    next.approvalUnactionable = false;
    next.nodes = endCalls(next.nodes).map((node) => {
      const isRunNode = node.id === next.runId;
      if (!node.streaming && !isRunNode) return node;
      return {
        ...node,
        streaming: false,
        truncated: node.truncated || (isRunNode && terminal === "finished" && next.cancelled),
        errored: node.errored || (isRunNode && terminal !== "finished"),
      };
    });
  }

  return next;
}

function applyApproval(next: ThreadState, frame: IntelligenceFrame): void {
  const request = approvalRequestOf(frame);
  if (request) {
    next.approval = request;
    next.approvalUnactionable = false;
    return;
  }
  if (unactionableApprovalFrame(frame)) {
    next.approvalUnactionable = true;
  }
}

function applyInvalidation(next: ThreadState, frame: IntelligenceFrame): boolean {
  const entities = entitiesInvalidatedBy(frame);
  if (!entities) return false;
  next.invalidated = mergeInvalidated(next.invalidated, entities);
  publishEntityInvalidation(frame, entities);
  return true;
}

export function approvalAnswered(state: ThreadState, toolCallId: string): ThreadState {
  if (state.approval === null || state.approval.toolCallId !== toolCallId) return state;
  return { ...state, approval: null, approvalUnactionable: false };
}

function settleApproval(next: ThreadState, frame: IntelligenceFrame): void {
  if (next.approval && approvalSettledBy(frame, next.approval.toolCallId)) {
    next.approval = null;
  }
}

function upstreamHasNoRun(notice: RelayNotice | null | undefined): boolean {
  return notice?.reason === "reattach-failed" && notice.status === 404;
}

export function shouldReattach(state: ThreadState): boolean {
  if (state.outcome !== "interrupted" || !state.runId) return false;
  return !upstreamHasNoRun(state.notice);
}

export type InterruptedClaim = "still-writing" | "unknown";

export function interruptedClaim(state: ThreadState): InterruptedClaim {
  return state.activeConfirmed ? "still-writing" : "unknown";
}

export function shouldReattachAfterDecision(state: ThreadState, streaming: boolean): boolean {
  if (streaming) return false;
  if (!state.runId) return false;
  if (state.outcome !== "running" && state.outcome !== "interrupted") return false;
  return !upstreamHasNoRun(state.notice);
}

export function assistantNode(state: ThreadState, runId = state.runId): ThreadNode | null {
  if (!runId) return null;
  return state.nodes.find((node) => node.id === runId && node.role === "assistant") ?? null;
}

export interface DocumentReadBack {
  settled: boolean;
  cancelled: boolean;
  unresolved: number;
}

export const ACTIVE_TURN_DELAYS_MS: readonly number[] = [1_500, 3_000, 6_000];

export function shouldAwaitActiveTurn(seen: DocumentReadBack | null): boolean {
  return seen !== null && !seen.settled;
}

export const DOCUMENT_HEAL_DELAYS_MS: readonly number[] = [2_000, 5_000, 10_000, 15_000];

export function shouldReadDocumentAgain(seen: DocumentReadBack | null): boolean {
  return Boolean(seen?.settled && !seen.cancelled && seen.unresolved > 0);
}

export function unresolvedTools(state: ThreadState, runId: string | null): ToolActivity[] {
  return (assistantNode(state, runId)?.tools ?? []).filter(isUnresolved);
}

export function resolvedByDocument(
  before: ThreadState,
  after: ThreadState,
  runId: string | null,
): ToolActivity[] {
  const knew = new Set(
    (assistantNode(before, runId)?.tools ?? []).filter(hasResult).map((tool) => tool.toolCallId),
  );
  return (assistantNode(after, runId)?.tools ?? []).filter(
    (tool) => hasResult(tool) && !knew.has(tool.toolCallId),
  );
}

export function recordDocumentHeal(
  state: ThreadState,
  healed: readonly ToolActivity[],
): ThreadState {
  const fresh = healed
    .map((tool) => tool.toolCallId)
    .filter((id) => id !== "" && !state.healedTools.includes(id));
  if (fresh.length === 0) return state;
  return { ...state, healedTools: [...state.healedTools, ...new Set(fresh)] };
}

export function documentHealFrame(): IntelligenceFrame {
  return { event: "lti-document-heal", data: "", id: null, retry: null, comment: null };
}

export function announceDocumentHeal(
  state: ThreadState,
  frame: IntelligenceFrame,
  entities: readonly InvalidatedEntity[],
): ThreadState {
  if (entities.length === 0) return state;
  publishEntityInvalidation(frame, entities);
  return { ...state, invalidated: mergeInvalidated(state.invalidated, entities) };
}

export function isThinking(state: ThreadState): boolean {
  if (state.outcome !== "running") return false;
  if (state.approval !== null) return false;
  const node = assistantNode(state);
  return node === null || node.text === "";
}
